import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PageTitle } from "../components/PageTitle";
import { sendOtp, verifyOtp } from "../api/login";
import { showErrorMessage, showSuccessMessage } from "../utils";
import { useCartStore } from "../store/cartStore";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/";

  const guestCartId = useCartStore((s) => s.cartId);
  const mergeCartAfterLogin = useCartStore((s) => s.mergeCartAfterLogin);

  const hasShownLoginMessage = useRef(false);

  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");

  // ── Already logged in ─────────────────────────────────
  useEffect(() => {
    if (localStorage.getItem("Token")) {
      navigate("/", { replace: true });
    }
  }, []);

  // ── Login message from redirect ───────────────────────
  useEffect(() => {
    const loginMessage = location.state?.message;
    if (!loginMessage || hasShownLoginMessage.current) return;
    hasShownLoginMessage.current = true;
    showSuccessMessage(loginMessage);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  // ── Step 1 — Send OTP ─────────────────────────────────
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError("");

    if (!email.trim()) {
      setError("Email is required");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Enter a valid email address");
      return;
    }

    setLoading(true);
    try {
      const res = await sendOtp({ email: email.trim().toLowerCase() });
      if (res?.status !== "ok") throw res?.error || "Could not send OTP";
      showSuccessMessage("OTP sent to your email!");
      setStep("otp");
    } catch (err) {
      setError(typeof err === "string" ? err : "Could not send OTP");
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2 — Verify OTP ───────────────────────────────
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");

    if (!otp.trim()) {
      setError("OTP is required");
      return;
    }
    if (otp.trim().length !== 6) {
      setError("Enter 6-digit OTP");
      return;
    }

    setLoading(true);
    try {
      const res = await verifyOtp({
        email: email.trim().toLowerCase(),
        otp: otp.trim(),
        guest_cart_id: guestCartId,
      });
      if (res?.status !== "ok") throw res?.error || "Invalid OTP";

      localStorage.setItem("Token", res.token);
      localStorage.setItem("userType", "Customer");

      if (res.cart_id) await mergeCartAfterLogin(res.cart_id);

      showSuccessMessage("Welcome to HueHoppers! 🌈");
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(typeof err === "string" ? err : "Invalid or expired OTP");
    } finally {
      setLoading(false);
    }
  };

  // ── Resend OTP ────────────────────────────────────────
  const handleResend = async () => {
    setResending(true);
    setError("");
    try {
      const res = await sendOtp({ email: email.trim().toLowerCase() });
      if (res?.status !== "ok") throw res?.error || "Could not resend OTP";
      showSuccessMessage("OTP resent!");
      setOtp("");
    } catch (err) {
      setError(typeof err === "string" ? err : "Could not resend OTP");
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <PageTitle
        title="Login"
        subtitle="Welcome back, Hopper! Your hue is waiting!"
        bgImage="default.png"
      />

      <section className="flat-spacing-10">
        <div className="container">
          <div style={{ maxWidth: 440, margin: "0 auto" }}>
            {/* ── Step 1 — Email ── */}
            {step === "email" && (
              <>
                <div className="flat-title mb_30 px-0">
                  <h5 className="mb_8">Welcome to HueHoppers</h5>
                  <p style={{ color: "#888", fontSize: 14 }}>
                    Enter your email — we'll send you a one-time password.
                  </p>
                </div>

                <form onSubmit={handleSendOtp}>
                  <div className="tf-field style-1 mb_20">
                    <input
                      className="tf-field-input tf-input"
                      type="email"
                      value={email}
                      placeholder=" " // ← yeh add karo
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError("");
                      }}
                      autoFocus
                    />
                    <label className="tf-field-label">Email Address *</label>
                  </div>

                  {error && (
                    <p
                      style={{
                        color: "#b42318",
                        fontSize: 13,
                        marginBottom: 12,
                      }}
                    >
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="tf-btn w-100 btn-fill"
                    style={{
                      opacity: loading ? 0.7 : 1,
                      cursor: loading ? "not-allowed" : "pointer",
                    }}
                  >
                    {loading ? "Sending OTP..." : "Send OTP →"}
                  </button>
                </form>
              </>
            )}

            {/* ── Step 2 — OTP ── */}
            {/* ── Step 2 — OTP ── */}
            {step === "otp" && (
              <>
                <div className="flat-title mb_30 px-0">
                  <h5 className="mb_8">Check your email</h5>
                  <p style={{ color: "#888", fontSize: 14 }}>
                    We sent a 6-digit OTP to <strong>{email}</strong>
                  </p>
                </div>

                <form onSubmit={handleVerifyOtp}>
                  {/* ── Simple input — no tf-field ── */}
                  <input
                    type="number"
                    value={otp}
                    min={"100000"}
                    max={"999999"}
                    placeholder="Enter OTP"
                    maxLength={6}
                    onChange={(e) => {
                      setOtp(e.target.value.replace(/\D/g, ""));
                      setError("");
                    }}
                    autoFocus
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      border: "1px solid #ddd",
                      fontSize: 22,
                      letterSpacing: 12,
                      textAlign: "center",
                      color: "#2c3a34",
                      marginBottom: 16,
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />

                  {error && (
                    <p
                      style={{
                        color: "#b42318",
                        fontSize: 13,
                        marginBottom: 12,
                      }}
                    >
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="tf-btn w-100 btn-fill mb_15"
                    style={{
                      opacity: loading ? 0.7 : 1,
                      cursor: loading ? "not-allowed" : "pointer",
                    }}
                  >
                    {loading ? "Verifying..." : "Verify OTP →"}
                  </button>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setStep("email");
                        setOtp("");
                        setError("");
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#888",
                        fontSize: 13,
                        cursor: "pointer",
                      }}
                    >
                      ← Change email
                    </button>
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={resending}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#3a00ac",
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      {resending ? "Resending..." : "Resend OTP"}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Login;
